import { useNavigate } from "react-router-dom";
import { ChevronRight, Users } from "lucide-react";
import { Skeleton } from "../../../../components/ui/skeleton";
import { EmptyState } from "../../hyperliquid/components/EmptyState";
import { WalletCell } from "../../hyperliquid/components/WalletCell";
import {
  formatDateTime,
  formatDecimal,
  formatInteger,
  formatUsd,
  truncateAddress,
} from "../../../../lib/format";
import { cn } from "../../../../lib/utils";

export function CombinedUsersTable({ users, isLoading }) {
  if (!isLoading && users && users.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-4 w-4" />}
        title="No registered users"
        description="No combined users found for this period."
      />
    );
  }

  return (
    <>
      {/* Mobile: card list */}
      <div className="md:hidden">
        <MobileList users={users} isLoading={isLoading} />
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="text-[10px] font-sans uppercase tracking-[1.5px] text-muted-foreground">
              <Th align="left">User</Th>
              <Th align="right">Fills</Th>
              <Th align="right">Volume</Th>
              <Th align="right">Total Fees</Th>
              <Th align="left">Last Activity</Th>
              <Th align="right" className="w-8"><span className="sr-only">Open</span></Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !users
              ? Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
              : users?.map((u) => <DesktopRow key={u.walletAddress} user={u} />)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DesktopRow({ user }) {
  const navigate = useNavigate();
  const active = user.combined.fillCount > 0;

  const hlTime = user.hyperliquid?.lastFillTime ? new Date(user.hyperliquid.lastFillTime) : null;
  const orderlyTime = user.orderly?.lastFillTime ? new Date(user.orderly.lastFillTime) : null;
  let lastActivity = null;
  if (hlTime && orderlyTime) lastActivity = hlTime > orderlyTime ? hlTime : orderlyTime;
  else if (hlTime) lastActivity = hlTime;
  else if (orderlyTime) lastActivity = orderlyTime;

  return (
    <tr
      onClick={() => navigate(`/web3/hyperliquid/fills/${user.walletAddress}`)}
      className="group cursor-pointer border-border transition-colors hover:bg-white/[0.02]"
    >
      <Td>
        <WalletCell address={user.walletAddress} active={active} className="group" />
      </Td>
      <Td align="right" muted={!active}>
        <span className="font-mono">{formatInteger(user.combined.fillCount)}</span>
      </Td>
      <Td align="right" muted={!active}>
        <span className="font-mono">
          {active ? formatUsd(user.combined.volume) : "—"}
        </span>
      </Td>
      <Td align="right" muted={!active}>
        <span className="font-mono">
          {active ? formatDecimal(user.combined.fees, 6) : "—"}
        </span>
      </Td>
      <Td muted={!active}>
        <span className="font-mono text-xs">
          {lastActivity ? formatDateTime(lastActivity) : "No activity"}
        </span>
      </Td>
      <Td align="right">
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 transition group-hover:translate-x-0.5 group-hover:text-ginox-green" />
      </Td>
    </tr>
  );
}

function MobileList({ users, isLoading }) {
  const navigate = useNavigate();

  if (isLoading && !users) {
    return (
      <ul className="divide-y divide-border/60">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="px-5 py-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-3 h-7 w-32" />
            <Skeleton className="mt-3 h-3 w-48" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="divide-y divide-border/60">
      {users?.map((u) => {
        const active = u.combined.fillCount > 0;
        const hlTime = u.hyperliquid?.lastFillTime ? new Date(u.hyperliquid.lastFillTime) : null;
        const orderlyTime = u.orderly?.lastFillTime ? new Date(u.orderly.lastFillTime) : null;
        let lastActivity = null;
        if (hlTime && orderlyTime) lastActivity = hlTime > orderlyTime ? hlTime : orderlyTime;
        else if (hlTime) lastActivity = hlTime;
        else if (orderlyTime) lastActivity = orderlyTime;

        return (
          <li key={u.walletAddress}>
            <button
              type="button"
              onClick={() => navigate(`/web3/hyperliquid/fills/${u.walletAddress}`)}
              className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.02] active:bg-white/[0.04]"
            >
              <span
                className={cn(
                  "mt-2 h-1.5 w-1.5 shrink-0 rounded-full",
                  active ? "bg-ginox-green" : "bg-white/10",
                )}
              />
              <div className="min-w-0 flex-1">
                <span className="font-mono text-sm text-foreground">
                  {truncateAddress(u.walletAddress, 8, 6)}
                </span>
                <p
                  className={cn(
                    "mt-2 font-mono text-[22px] font-medium leading-none tracking-tight",
                    active ? "text-foreground" : "text-muted-foreground/40",
                  )}
                  data-tabular
                >
                  {active ? formatUsd(u.combined.volume) : "$0.00"}
                </p>
                <div
                  className={cn(
                    "mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground",
                    !active && "text-muted-foreground/50",
                  )}
                >
                  <span>
                    <span className="font-mono">{formatInteger(u.combined.fillCount)}</span> fills
                  </span>
                  <span className="text-muted-foreground/30">·</span>
                  <span>
                    Fees{" "}
                    <span className="font-mono">
                      {active ? formatDecimal(u.combined.fees, 6) : "—"}
                    </span>
                  </span>
                  <span className="text-muted-foreground/30">·</span>
                  <span className="font-mono">
                    {lastActivity ? formatDateTime(lastActivity) : "No activity"}
                  </span>
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Th({ children, align = "left", className = "" }) {
  return (
    <th
      className={
        "border-b border-border/70 px-5 py-3 font-medium " +
        (align === "right" ? "text-right" : "text-left") +
        " " +
        className
      }
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", muted = false }) {
  return (
    <td
      className={
        "border-b border-border/40 px-5 py-3.5 text-sm " +
        (align === "right" ? "text-right" : "text-left") +
        (muted ? " text-muted-foreground/60" : "")
      }
    >
      {children}
    </td>
  );
}

function RowSkeleton() {
  return (
    <tr>
      <td className="border-b border-border/40 px-5 py-3.5">
        <Skeleton className="h-4 w-40" />
      </td>
      <td className="border-b border-border/40 px-5 py-3.5 text-right">
        <Skeleton className="ml-auto h-4 w-12" />
      </td>
      <td className="border-b border-border/40 px-5 py-3.5 text-right">
        <Skeleton className="ml-auto h-4 w-24" />
      </td>
      <td className="border-b border-border/40 px-5 py-3.5 text-right">
        <Skeleton className="ml-auto h-4 w-20" />
      </td>
      <td className="border-b border-border/40 px-5 py-3.5">
        <Skeleton className="h-4 w-32" />
      </td>
      <td className="border-b border-border/40 px-5 py-3.5" />
    </tr>
  );
}
